import { supabase } from "@/lib/supabase";

export interface DashboardStats {
  totalPreviews: number;
  sharablePreviews: number;
  totalProjects: number;
  activeProjects: number;
  archivedProjects: number;
  lastSeenPreview?: {
    name: string;
    lastSeenAt: string;
  } | null;
  averageViewTime: number; // in seconds
  totalViews: number;
  uniqueVisitors: number;
}

export interface MostUsedModel {
  model_uuid: string;
  model_name: string;
  usage_count: number;
  recent_usage: string;
}

export interface MostViewedPreview {
  preview_id: string;
  preview_name: string;
  model_name: string;
  total_views: number;
  unique_viewers: number;
  avg_duration: number;
  last_viewed: string;
  is_sharable: boolean;
}

export interface RecentViewerActivity {
  preview_id: string;
  preview_name: string;
  viewer_country: string;
  viewer_device: string;
  view_duration: number;
  viewed_at: string;
}

export interface EnhancedDashboardStats {
  total_previews: number;
  sharable_previews: number;
  total_projects: number;
  active_projects: number;
  archived_projects: number;
  total_views: number;
  unique_visitors: number;
  avg_view_duration: number;
  most_active_day: {
    date: string;
    views: number;
  } | null;
  growth_this_month: {
    current_month: number;
    last_month: number;
    growth_rate: number;
  } | null;
  top_performing_preview: {
    id: string;
    name: string;
    views: number;
  } | null;
  most_used_model: {
    name: string;
    usage_count: number;
  } | null;
}

export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get preview statistics with names for last seen functionality
    const { data: previewStats, error: previewError } = await supabase
      .from('previews')
      .select('id, name, is_sharable, created_at')
      .eq('user_id', user.id);

    if (previewError) throw previewError;

    // Get project statistics
    const { data: projectStats, error: projectError } = await supabase
      .from('projects')
      .select('is_archived')
      .eq('user_id', user.id);

    if (projectError) throw projectError;

    // Calculate basic statistics
    const totalPreviews = previewStats?.length || 0;
    const sharablePreviews = previewStats?.filter(p => p.is_sharable).length || 0;
    const totalProjects = projectStats?.length || 0;
    const activeProjects = projectStats?.filter(p => !p.is_archived).length || 0;
    const archivedProjects = projectStats?.filter(p => p.is_archived).length || 0;    // Calculate recent activity (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Get analytics data for all user's previews
    let totalViews = 0;
    let uniqueVisitors = 0;
    let averageViewTime = 0;
    let lastSeenPreview = null;

    if (totalPreviews > 0) {
      try {
        // Get analytics for all user previews
        const previewIds = previewStats?.map(p => p.id) || [];
        
        // Get total views and unique visitors across all previews
        const { data: analyticsData } = await supabase
          .from('preview_analytics')
          .select('preview_id, total_duration_seconds, fingerprint_hash, last_seen_at')
          .in('preview_id', previewIds);

        if (analyticsData && analyticsData.length > 0) {
          totalViews = analyticsData.length;
          
          // Count unique visitors using fingerprint_hash
          const uniqueFingerprints = new Set(
            analyticsData
              .map(a => a.fingerprint_hash)
              .filter(hash => hash !== null)
          );
          uniqueVisitors = uniqueFingerprints.size;

          // Calculate average view time
          const totalDuration = analyticsData.reduce((sum, record) => 
            sum + (record.total_duration_seconds || 0), 0
          );
          averageViewTime = totalViews > 0 ? Math.round(totalDuration / totalViews) : 0;

          // Find last seen preview
          const mostRecentView = analyticsData
            .sort((a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime())[0];
          
          if (mostRecentView) {
            const previewName = previewStats?.find(p => p.id === mostRecentView.preview_id)?.name;
            if (previewName) {
              lastSeenPreview = {
                name: previewName,
                lastSeenAt: mostRecentView.last_seen_at
              };
            }
          }
        }
      } catch (analyticsError) {
        console.error("Error fetching analytics data:", analyticsError);
        // Continue with default values if analytics fails
      }
    }    return {
      totalPreviews,
      sharablePreviews,
      totalProjects,
      activeProjects,
      archivedProjects,
      lastSeenPreview,
      averageViewTime,
      totalViews,
      uniqueVisitors,
    };
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);    return {
      totalPreviews: 0,
      sharablePreviews: 0,
      totalProjects: 0,
      activeProjects: 0,
      archivedProjects: 0,
      lastSeenPreview: null,
      averageViewTime: 0,
      totalViews: 0,
      uniqueVisitors: 0,
    };
  }
};

export const getEnhancedDashboardStats = async (): Promise<EnhancedDashboardStats> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase.rpc('get_enhanced_dashboard_stats', {
      p_user_id: user.id
    });

    if (error) throw error;

    const stats = data?.[0];
    if (!stats) {
      throw new Error('No dashboard statistics found');
    }

    return {
      total_previews: stats.total_previews || 0,
      sharable_previews: stats.sharable_previews || 0,
      total_projects: stats.total_projects || 0,
      active_projects: stats.active_projects || 0,
      archived_projects: stats.archived_projects || 0,
      total_views: stats.total_views || 0,
      unique_visitors: stats.unique_visitors || 0,
      avg_view_duration: stats.avg_view_duration || 0,
      most_active_day: stats.most_active_day || null,
      growth_this_month: stats.growth_this_month || null,
      top_performing_preview: stats.top_performing_preview || null,
      most_used_model: stats.most_used_model || null,
    };
  } catch (error) {
    console.error("Error fetching enhanced dashboard stats:", error);
    return {
      total_previews: 0,
      sharable_previews: 0,
      total_projects: 0,
      active_projects: 0,
      archived_projects: 0,
      total_views: 0,
      unique_visitors: 0,
      avg_view_duration: 0,
      most_active_day: null,
      growth_this_month: null,
      top_performing_preview: null,
      most_used_model: null,
    };
  }
};

// Fix getMostViewedPreviews function
export async function getMostViewedPreviews(limit: number = 10): Promise<MostViewedPreview[]> {
    try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        // Get user's sharable previews
        const { data: previews, error: previewError } = await supabase
            .from('previews')
            .select(`
                id,
                name,
                is_sharable,
                available_models!inner(name)
            `)
            .eq('user_id', user.id)
            .eq('is_sharable', true);

        if (previewError) throw previewError;
        if (!previews || previews.length === 0) return [];

        const previewIds = previews.map(p => p.id);

        // Get analytics data for these previews
        const { data: analyticsData, error: analyticsError } = await supabase
            .from('preview_analytics')
            .select(`
                preview_id,
                total_duration_seconds,
                fingerprint_hash,
                last_seen_at
            `)
            .in('preview_id', previewIds);

        if (analyticsError) throw analyticsError;

        // Group analytics by preview
        const previewAnalytics = new Map<string, any[]>();
        
        if (analyticsData) {
            analyticsData.forEach(record => {
                const previewId = record.preview_id;
                if (!previewAnalytics.has(previewId)) {
                    previewAnalytics.set(previewId, []);
                }
                previewAnalytics.get(previewId)!.push(record);
            });
        }

        const result = previews
            .map(preview => {
                const views = previewAnalytics.get(preview.id) || [];
                const totalViews = views.length;
                
                if (totalViews === 0) return null;

                const uniqueViewers = new Set(
                    views
                        .map((v: any) => v.fingerprint_hash)
                        .filter((hash: any) => hash !== null)
                ).size;
                
                const avgDuration = views.length > 0 
                    ? views.reduce((sum: number, view: any) => sum + (view.total_duration_seconds || 0), 0) / views.length 
                    : 0;
                
                const lastViewed = views.length > 0 
                    ? Math.max(...views.map((v: any) => new Date(v.last_seen_at).getTime()))
                    : null;

                // Handle both array and object types for available_models
                const modelName = (preview.available_models as any)?.name || 'Unknown Model';

                return {
                    preview_id: preview.id,
                    preview_name: preview.name,
                    model_name: modelName,
                    total_views: totalViews,
                    unique_viewers: uniqueViewers,
                    avg_duration: avgDuration,
                    last_viewed: lastViewed ? new Date(lastViewed).toISOString() : new Date().toISOString(),
                    is_sharable: preview.is_sharable
                };
            })
            .filter((item): item is MostViewedPreview => item !== null)
            .sort((a, b) => b.total_views - a.total_views)
            .slice(0, limit);

        return result;
    } catch (error) {
        console.error('Error fetching most viewed previews:', error);
        return [];
    }
};

export const getMostUsedModels = async (limit = 5): Promise<MostUsedModel[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // First try the RPC function
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_most_used_models', {
      p_user_id: user.id,
      p_limit: limit
    });

    if (rpcError) {
      console.warn('RPC function not available, falling back to direct query:', rpcError.message);
      
      // Fallback to direct query
      const { data: previewData } = await supabase
        .from('previews')
        .select('model_uuid, available_models!inner(name), created_at')
        .eq('user_id', user.id)
        .not('model_uuid', 'is', null);

      if (!previewData || previewData.length === 0) {
        return [];
      }

      // Count model usage
      const modelUsage = new Map();
      previewData.forEach(preview => {
        const modelId = preview.model_uuid;
        // Handle both array and object types for available_models
        const modelName = (preview.available_models as any)?.name || 'Unknown Model';
        
        if (!modelUsage.has(modelId)) {
          modelUsage.set(modelId, {
            model_uuid: modelId,
            model_name: modelName,
            usage_count: 0,
            recent_usage: preview.created_at
          });
        }
        
        const existing = modelUsage.get(modelId);
        existing.usage_count++;
        
        // Keep the most recent usage date
        if (new Date(preview.created_at) > new Date(existing.recent_usage)) {
          existing.recent_usage = preview.created_at;
        }
      });

      // Convert to array and sort by usage count
      return Array.from(modelUsage.values())
        .sort((a, b) => b.usage_count - a.usage_count)
        .slice(0, limit);
    }

    return rpcData || [];
  } catch (error) {
    console.error("Error fetching most used models:", error);
    return [];
  }
};

export const getRecentViewerActivity = async (limit = 10): Promise<RecentViewerActivity[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // First try the RPC function
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_recent_viewer_activity', {
      p_user_id: user.id,
      p_limit: limit
    });

    if (rpcError) {
      console.warn('RPC function not available, falling back to direct query:', rpcError.message);
      
      // Fallback to direct query
      const { data: previewData } = await supabase
        .from('previews')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('is_sharable', true);

      if (!previewData || previewData.length === 0) {
        return [];
      }

      const previewIds = previewData.map(p => p.id);
      
      const { data: analyticsData } = await supabase
        .from('preview_analytics')
        .select(`
          preview_id,
          country_code,
          device_type,
          total_duration_seconds,
          last_seen_at
        `)
        .in('preview_id', previewIds)
        .order('last_seen_at', { ascending: false })
        .limit(limit);

      if (!analyticsData) {
        return [];
      }

      // Map analytics data to activity format
      return analyticsData.map(record => {
        const preview = previewData.find(p => p.id === record.preview_id);
        
        return {
          preview_id: record.preview_id,
          preview_name: preview?.name || 'Unknown Preview',
          viewer_country: record.country_code || 'Unknown',
          viewer_device: record.device_type || 'Unknown',
          view_duration: record.total_duration_seconds || 0,
          viewed_at: record.last_seen_at
        };
      });
    }

    return rpcData || [];
  } catch (error) {
    console.error("Error fetching recent viewer activity:", error);
    return [];
  }
};

/**
 * Get views over time data for charts
 */
export async function getViewsOverTimeData(days: number = 30) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // First try the RPC function
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_views_over_time', {
      p_user_id: user.id,
      p_days: days
    })

    if (rpcError) {
      console.warn('RPC function not available, falling back to direct query:', rpcError.message)
      
      // Fallback to direct query if RPC doesn't exist
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)

      const { data: previewData } = await supabase
        .from('previews')
        .select('id')
        .eq('user_id', user.id)

      if (!previewData || previewData.length === 0) {
        return []
      }

      const previewIds = previewData.map(p => p.id)
      
      const { data: analyticsData } = await supabase
        .from('preview_analytics')
        .select('first_seen_at, preview_id, fingerprint_hash')
        .in('preview_id', previewIds)
        .gte('first_seen_at', startDate.toISOString())
        .lte('first_seen_at', endDate.toISOString())

      // Group by date
      const dateGroups = new Map()
      
      if (analyticsData) {
        analyticsData.forEach(record => {
          const date = new Date(record.first_seen_at).toISOString().split('T')[0]
          if (!dateGroups.has(date)) {
            dateGroups.set(date, {
              views: new Set(),
              uniqueVisitors: new Set()
            })
          }
          
          dateGroups.get(date).views.add(record.preview_id + '-' + record.first_seen_at)
          if (record.fingerprint_hash) {
            dateGroups.get(date).uniqueVisitors.add(record.fingerprint_hash)
          }
        })
      }

      // Convert to array format
      const result = []
      for (let i = 0; i < days; i++) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        const dateStr = date.toISOString().split('T')[0]
        
        const dayData = dateGroups.get(dateStr)
        result.unshift({
          date: dateStr,
          views: dayData ? dayData.views.size : 0,
          uniqueVisitors: dayData ? dayData.uniqueVisitors.size : 0
        })
      }

      return result
    }

    // Ensure data is properly formatted and validated
    return (rpcData || []).map((item: any) => ({
      date: item.date || new Date().toISOString().split('T')[0],
      views: Number(item.views) || 0,
      uniqueVisitors: Number(item.unique_visitors) || 0
    }))
  } catch (error) {
    console.error('Error fetching views over time:', error)
    return []
  }
}

/**
 * Get geographic distribution data
 */
export async function getGeographicDistribution() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // First try the RPC function
    const { data: rpcData, error: rpcError } = await supabase.rpc('get_geographic_distribution', {
      p_user_id: user.id
    })

    if (rpcError) {
      console.warn('RPC function not available, falling back to direct query:', rpcError.message)
      
      // Fallback to direct query
      const { data: previewData } = await supabase
        .from('previews')
        .select('id')
        .eq('user_id', user.id)

      if (!previewData || previewData.length === 0) {
        return []
      }

      const previewIds = previewData.map(p => p.id)
      
      const { data: analyticsData } = await supabase
        .from('preview_analytics')
        .select('country_code')
        .in('preview_id', previewIds)
        .not('country_code', 'is', null)

      // Count by country
      const countryCount = new Map()
      let totalViews = 0

      if (analyticsData) {
        analyticsData.forEach(record => {
          const country = record.country_code || 'Unknown'
          countryCount.set(country, (countryCount.get(country) || 0) + 1)
          totalViews++
        })
      }

      // Convert to array with percentages
      const result = Array.from(countryCount.entries())
        .map(([country, views]) => ({
          country,
          views,
          percentage: totalViews > 0 ? Math.round((views / totalViews) * 100) : 0
        }))
        .sort((a, b) => b.views - a.views)

      return result
    }

    // Validate and format geographic data
    return (rpcData || []).map((item: any) => ({
      country: item.country || 'Unknown',
      views: Number(item.views) || 0,
      percentage: Number(item.percentage) || 0
    }))
  } catch (error) {
    console.error('Error fetching geographic distribution:', error)
    return []
  }
}

/**
 * Get device usage statistics
 */
export async function getDeviceUsageStats() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.rpc('get_device_usage_stats', {
      p_user_id: user.id
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching device usage stats:', error)
    return []
  }
}

/**
 * Get engagement metrics for top previews
 */
export async function getEngagementMetrics(limit: number = 10) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.rpc('get_engagement_metrics', {
      p_user_id: user.id,
      p_limit: limit
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching engagement metrics:', error)
    return []
  }
}

/**
 * Get hourly activity data
 */
export async function getHourlyActivity() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    const { data, error } = await supabase.rpc('get_hourly_activity', {
      p_user_id: user.id
    })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching hourly activity:', error)
    return []
  }
}
